import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { styled } from "@mui/system";
import { Box, Collapse, IconButton, Stack, SxProps, Theme, Tooltip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import theme from "common/theme/theme";
import { LanguageContext } from "common/contexts/LanguageContext";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  backgroundColor: theme.palette.primary.main,
}));

interface ToolbarSectionProps {
  title: string;
  children: React.ReactNode;
}

export const GetFormControlSxProps = (): SxProps<Theme> => {
  return {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "32px",
    ml: "0px",
    mr: "4px",
  };
};
const ToolbarSection: FC<ToolbarSectionProps> = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isContracting, setIsContracting] = useState(false);

  const isMouseWithinExpandIcon = useRef(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const { getString } = useContext(LanguageContext);

  const handleSectionContracting = () => {
    // Clear any existing timeout
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
    }

    // Set a new timeout to change the state after a delay
    timeoutIdRef.current = setTimeout(() => {
      setIsContracting(false);
    }, 1000); // Delay in milliseconds
  };

  // Clean up the timeout if the component unmounts
  useEffect(() => {
    return () => {
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  const handleMouseEnterExpandIcon = () => {
    isMouseWithinExpandIcon.current = true;
    // Set a timer to show the tooltip after  500ms
    const timerId = setTimeout(() => {
      // Check if the mouse is still over the button
      if (isMouseWithinExpandIcon.current) {
        if (!isContracting) {
          setIsExpanded(true);
        }
      }
    }, 200);

    // Clear the timer if the mouse leaves the button
    return () => clearTimeout(timerId);
  };

  const handleMouseClickContract = () => {
    setIsExpanded(false);
    handleSectionContracting();
  };

  return (
    <>
      <Container
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: "-4px",
        }}
      >
        <Collapse in={!isExpanded} orientation="horizontal">
          <Stack direction="row">
            <Typography sx={{ pr: "4px", pt: "4px" }}>{title}</Typography>
            <ChevronRightIcon sx={{ mt: "4px" }} onMouseEnter={handleMouseEnterExpandIcon} />
          </Stack>
        </Collapse>
        <Collapse in={isExpanded} orientation="horizontal">
          <Stack direction="column" sx={{ ml: "-10px" }}>
            <Stack direction="row" height="16px" display="flex" justifyContent="space-between">
              <Typography sx={{ fontSize: "12px" }}>{title}</Typography>
              <Tooltip title={getString('tip_contract_section')}>
                <IconButton
                  onClick={handleMouseClickContract}
                  sx={{
                    mr: "-10px",
                    color: theme.palette.common.white,
                    "&:hover": {
                      color: theme.palette.primary.light,
                    },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack direction="row" height="32px" sx={{ pt: "4px" }}>
              <div style={{ display: "flex" }}>{children}</div>
            </Stack>
          </Stack>
        </Collapse>
      </Container>
    </>
  );
};

export default ToolbarSection;
